// services/collectorActions.service.js
//
// The collector's writes (collector-app P1-C part C) — plain-JSON endpoints,
// not JSON:API (backend brief 2026-09-30-collector-actions):
//   - POST /collector-actions  — a batch of taps; one answer per action, in the
//     order sent: `{ id, result, state, reason, current }`.
//   - POST /collector-photos   — one shelf photo (multipart); the id a `facts`
//     tap attaches. Idempotent by the phone's uuid: a resend gets the same id.
//   - POST /collector-shop-invoices — «سجّل وخلّص المحل»: the shop's invoice
//     (amount, or `noPaper`), with an optional photo of the paper.
//
// The contract lives in the backend: the docblock of
// ../backend/app/Services/Commerce/CollectorActionApplier.php and
// ../backend/app/Http/Requests/Api/V1/Collectors/CollectorActionsRequest.php.
//
// apiClient ONLY — the Sanctum cookie, X-XSRF-TOKEN and the shared 401/419
// interceptors flow through it. Never a new axios instance
// (src/httpConfig/config/axios.js).
import { apiClient } from '@/httpConfig/config/axios.js';

/** The backend refuses a bigger batch whole (422). */
export const MAX_ACTIONS_PER_BATCH = 200;

/**
 * A tap's request timeout. Shorter than the app's 30 s default: a collector
 * standing at a shelf should see «أوفلاين» in seconds, and a timed-out batch
 * is simply sent again (every action is idempotent).
 */
export const ACTIONS_TIMEOUT_MS = 15_000;

/**
 * Send one batch. Only the contract fields go on the wire — whatever else the
 * phone keeps on an action stays on the phone.
 *
 * @param {Array<{id: string, planItemId: string|number, type: string, payload: object, clientAt: string, seq: number}>} actions
 * @returns {Promise<Array<{id: string, result: string, state: string|null, reason: string|null, current: object|null}>>}
 */
export async function postCollectorActions(actions) {
  const body = {
    actions: actions.map(({ id, planItemId, type, payload, clientAt, seq }) => ({
      id,
      planItemId: Number(planItemId),
      type,
      payload: payload ?? {},
      clientAt,
      seq,
    })),
  };
  const response = await apiClient.post('/collector-actions', body, { timeout: ACTIONS_TIMEOUT_MS });
  const answers = response?.data?.data;
  return Array.isArray(answers) ? answers : [];
}

/**
 * Upload one shelf photo. Upload is not attach: send the returned `id` in a
 * `facts` action's `photoIds`.
 *
 * @param {{ uuid: string, planItemId: string|number, file: Blob, filename?: string }} input
 * @returns {Promise<{ id: string, url: string }>}
 */
export async function postCollectorPhoto({ uuid, planItemId, file, filename = 'shelf.jpg' }) {
  const form = new FormData();
  form.append('uuid', uuid);
  form.append('planItemId', String(planItemId));
  form.append('file', file, filename);
  const response = await apiClient.post('/collector-photos', form, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60_000,
  });
  return response?.data?.data ?? null;
}

/**
 * File the shop's invoice («سجّل وخلّص المحل»): the paper's total, or
 * `noPaper: true` when the shop gave none. JSON when there is no photo,
 * multipart when there is (the backend reads both). Not a queued tap — phase 1
 * files it online and waits for the answer; `applied` / `conflict
 * {leader_closed}` / `refused {plan_cancelled|plan_not_started}` come back as
 * `{ result, state, reason, invoice }`.
 *
 * @param {{ planId: string|number, supplierId: string|number, amountCents?: number|null, noPaper?: boolean, photo?: Blob|null }} input
 * @returns {Promise<{ result: string, state: string|null, reason: string|null, invoice: object|null }>}
 */
export async function postCollectorShopInvoice({ planId, supplierId, amountCents = null, noPaper = false, photo = null }) {
  const fields = { planId: Number(planId), supplierId: Number(supplierId) };
  if (noPaper) fields.noPaper = true;
  else fields.amountCents = Number(amountCents);

  let response;
  if (photo) {
    const form = new FormData();
    for (const [name, value] of Object.entries(fields)) form.append(name, value === true ? '1' : String(value));
    form.append('photo', photo, 'invoice.jpg');
    response = await apiClient.post('/collector-shop-invoices', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60_000,
    });
  } else {
    response = await apiClient.post('/collector-shop-invoices', fields, { timeout: 30_000 });
  }
  return response?.data?.data ?? null;
}
